import { MouseEvent, useEffect, useState } from 'react';
import Modal from '@/common/Modal';
import PlayerContract from '../PlayerContract';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { getUser } from '@/library/UserSessionHelper';
import { getContractStatus, ITourContractSettings } from '@/library/ContractSettingsHelper';
import { PlayerForSaveDto } from '@/api/models';
import { Contract } from '@/api/models/PlayerContractHistory';
import { isMobileResolution } from '@/utils';
import InformationIcon from '@/assets/icons/information.svg?react';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import styles from './PlayerTable.module.scss';

interface IPlayerTableProps {
  players: PlayerForSaveDto[];
}

function PlayerTable({ players }: IPlayerTableProps) {
  const [showModal, setShowModal] = useState(false);
  const [modalPlayer, setModalPlayer] = useState({} as PlayerForSaveDto);
  const [enableEdit, setEnableEdit] = useState(false);
  const [enableHistory, setEnableHistory] = useState(false);
  const [playerList, setPlayerList] = useState(players);

  const { goToPlayerPage } = useAppNavigation();

  useEffect(() => {
    setPlayerList(players);
  }, [players]);

  useEffect(() => {
    const getContractSettings = async () => {
      const user = await getUser();
      let contractSettings = user?.contractSettings as ITourContractSettings;
      //When an admin user has history they will always have edit access as well. We really only needed a single Admin group, but we'll check for both just to be thorough
      if (contractSettings) {
        setEnableEdit(contractSettings.contractEdit);
        setEnableHistory(contractSettings.contractHistory);
      }
    };
    getContractSettings();
  }, []);

  const handleInformationModal = (event: MouseEvent<HTMLButtonElement>, player: PlayerForSaveDto) => {
    setModalPlayer(player);
    setShowModal(true);
    event.stopPropagation();
  };

  const handleOnSave = (contract: Contract) => {
    //when doing a double-click, then contract object exists but has no properties. So check for existance of endDate property
    if (contract && contract.endDate) {
      const newPlayers = players.map((player) => {
        if (player.playerId == modalPlayer.playerId) {
          return {
            ...player,
            contract: {
              ...player.contract,
              startDate: contract.startDate,
              endDate: contract.endDate,
              items: contract.items,
              status: contract.status,
            },
          };
        }
        return player;
      });
      setPlayerList(newPlayers);
    }
  };

  return (
    <div className={styles.playerTable}>
      <div className={styles.mobilePlayerTable}>
        <div className={styles.tableHeader}>
          {/* Define method to go to player page */}
          <div className={styles.playerName}>Player Name</div>
          <div>Account</div>
          <div>Status</div>
          <div>Internal Status</div>
          <div>Region</div>
          {enableEdit && !isMobileResolution && <div>Contract</div>}
        </div>
        {playerList.map((player, i) => (
          <div key={player.playerId} className={styles.tableRow} role="button" data-testid={`tourbag-player-table-row-${i + 1}`} onClick={() => goToPlayerPage({ playerId: player.playerId })}>
            <div className={styles.playerName}>{player.fullName}</div>
            <div>{player.defaultTourName}</div>
            <div>{player.playerBioDto.playerStatusLookupName}</div>
            <div>{player.playerBioDto.playerInternalStatusLookupName}</div>
            <div>{player.playerBioDto.playerRegionLookupName}</div>
            {enableEdit && !isMobileResolution && (
              <div className={styles.contractStatus} data-testid={`tourbag-player-contract-cell-${i + 1}`}>
                {getContractStatus(player?.contract)}
                <button type="button" className={styles.informationIconButton} aria-label="Open player contract" onClick={(e) => handleInformationModal(e, player)}>
                  <InformationIcon />
                </button>
              </div>
            )}
          </div>
        ))}
        {/* {!players.length && <div className={styles.noResults}>No available players with the current filters.</div>} */}
      </div>
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} className={styles.modal}>
          <button type="button" className={styles.closeButton} aria-label="Close contract modal" onClick={() => setShowModal(false)}>
            <CloseIcon aria-hidden="true" className={styles.closeIcon} />
          </button>
          <PlayerContract player={modalPlayer} showModal={showModal} onSave={(contract) => handleOnSave(contract)} enableEdit={enableEdit} enableHistory={enableHistory} />
        </Modal>
      )}
    </div>
  );
}

export default PlayerTable;
